import intersection from 'lodash/intersection';
import difference from 'lodash/difference';

import { CATEGORIES } from '../constants/universes';
import {
  CLEANING_PRODUCTS,
  CLEANING_RECURRING,
  EYE_CARE,
  FACE_CARE_PRESTATIONS,
  FRENCH_SUPPLEMENT_HANDS,
  MANICURE,
  NAIL_POLISH,
  HANDS_GEL_NAIL_POLISH,
  PEDICURE_PRESTATIONS,
  FRENCH_SUPPLEMENT_FEET,
  FEET_PERMANENT_NAIL_POLISH,
  HAIR_REMOVAL_BIKINI_LINE_FULL,
  HAIR_REMOVAL_INTERGLUTEAL_CLEFT,
  HAIRCARE_PATINA,
  MAN_COLOR,
  MAN_SHAMPOO,
  WOMAN_SHAMPOO,
  WOMAN_HAIR_HIGHLIGHTING,
  WOMAN_STREAKS,
  HAIRCUT_WOMAN_TECHNICAL_PRESTATIONS_WITHOUT_PATINA,
  BABYBOOMER_HANDS,
  BABYBOOMER_FEET,
  FEET_NAIL_POLISH,
  CLEANING_SPOT,
} from '../constants/prestations';

type PrestationsRulesParams = {
  prestationsWithDependencies: string[];
  dependencies: string[];
};

/**
 * This function allows to add prestationsWithDependencies
 * only if there is at least one prestation inside
 * dependencies that is included in the prestations of your basket
 *
 * Exemple:
 * dependencies = [
 *    "radiance_care",
 *    "purifying_care",
 *    "face_care_scrub",
 *    "face_care_anti_ageing",
 *    "face_care_moisturising"
 * ]
 * prestationsWithDependencies= "eye_care"
 * prestations = ["eye_care"]
 *
 * You will not be able to add the "eye_care" prestation if there is not at least
 * one prestation of dependencies in the basket
 */
const addOnlyIfAtLeastOneOtherPrestation =
  ({ prestationsWithDependencies, dependencies }: PrestationsRulesParams) =>
  (prestations: string[]) => {
    if (
      intersection(prestations, prestationsWithDependencies).length > 0 &&
      intersection(prestations, dependencies).length === 0
    ) {
      return difference(prestations, prestationsWithDependencies);
    }
    return prestations;
  };

/**
 * This function adds all the missing dependencies
 * when we add one of the prestationsWithDependencies in prestations
 *
 * prestationsWithDependencies = [
 *  "woman_color", "woman_fullcolor", "tie_and_dye",
 *  "woman_streaks", "woman_hair_highlighting", "woman_hair_permanent"
 * ]
 *
 * dependencies = [ "woman_shampoo", "haircare_patina" ]
 *
 * prestations = [ "tie_and_dye" ]
 *
 * if you add "tie_and_dye" to your basket then the "woman_shampoo" and
 * "haircare_patina" will be added
 * The user CAN remove "woman_shampoo" and "haircare_patina" if he wants to
 */
const addSuggestedPrestation =
  ({ prestationsWithDependencies, dependencies }: PrestationsRulesParams) =>
  (prestations: string[], newPrestation?: string) => {
    if (!newPrestation || !prestationsWithDependencies.includes(newPrestation)) return prestations;

    // Only the missing prestations, to avoid duplicating prestations.
    return prestations.concat(difference(dependencies, prestations));
  };

const addFirstSuggestedPrestation =
  ({ prestationsWithDependencies, dependencies }: PrestationsRulesParams) =>
  (prestations: string[], newPrestation?: string) => {
    if (!newPrestation || !prestationsWithDependencies.includes(newPrestation)) return prestations;
    if (prestations.some((ref) => dependencies.includes(ref))) return prestations;

    // Only the missing prestations, to avoid duplicating prestations.
    return prestations.concat(difference([dependencies[0]], prestations));
  };

/**
 * This function adds the dependencies when the prestations you try to add
 * is a prestation with dependency
 *
 * Exemple:
 * prestations = [ "french_supplement_hands" ]
 * prestationsWithDependencies = [ "french_supplement_hands" ]
 * dependencies = "nail_polish"
 *
 * if you add to your basket "french_supplement_hands" then the "nail_polish" will be added
 * The user cannot remove "nail_polish"
 */
const addRequiredPrestation =
  ({ prestationsWithDependencies, dependencies }: PrestationsRulesParams) =>
  (prestations: string[]) => {
    if (!prestations.some((ref) => prestationsWithDependencies.includes(ref))) return prestations;

    // Only the missing prestations, to avoid duplicating prestations.
    return prestations.concat(difference(dependencies, prestations));
  };

const addFirstRequiredPrestation =
  ({ prestationsWithDependencies, dependencies }: PrestationsRulesParams) =>
  (prestations: string[]) => {
    if (!prestations.some((ref) => prestationsWithDependencies.includes(ref))) return prestations;
    if (prestations.some((ref) => dependencies.includes(ref))) return prestations;

    // Only the missing prestations, to avoid duplicating prestations.
    return prestations.concat(difference([dependencies[0]], prestations));
  };

export default {
  [CATEGORIES.WOMAN]: [
    addRequiredPrestation({
      prestationsWithDependencies: HAIRCUT_WOMAN_TECHNICAL_PRESTATIONS_WITHOUT_PATINA,
      dependencies: [WOMAN_SHAMPOO],
    }),
    addSuggestedPrestation({
      prestationsWithDependencies: [WOMAN_HAIR_HIGHLIGHTING, WOMAN_STREAKS],
      dependencies: [HAIRCARE_PATINA],
    }),
  ],
  [CATEGORIES.MAN]: [
    addSuggestedPrestation({
      prestationsWithDependencies: [MAN_COLOR],
      dependencies: [MAN_SHAMPOO],
    }),
  ],
  [CATEGORIES.BODYCARE]: [
    addFirstRequiredPrestation({
      prestationsWithDependencies: [FRENCH_SUPPLEMENT_HANDS],
      dependencies: [HANDS_GEL_NAIL_POLISH, NAIL_POLISH],
    }),
    addFirstRequiredPrestation({
      prestationsWithDependencies: [BABYBOOMER_HANDS],
      dependencies: [HANDS_GEL_NAIL_POLISH, NAIL_POLISH],
    }),
    addSuggestedPrestation({
      prestationsWithDependencies: [HANDS_GEL_NAIL_POLISH],
      dependencies: [MANICURE],
    }),
    addFirstRequiredPrestation({
      prestationsWithDependencies: [FRENCH_SUPPLEMENT_FEET],
      dependencies: [FEET_PERMANENT_NAIL_POLISH, FEET_NAIL_POLISH],
    }),
    addFirstRequiredPrestation({
      prestationsWithDependencies: [BABYBOOMER_FEET],
      dependencies: [FEET_PERMANENT_NAIL_POLISH, FEET_NAIL_POLISH],
    }),
    addFirstSuggestedPrestation({
      prestationsWithDependencies: [FEET_PERMANENT_NAIL_POLISH],
      dependencies: PEDICURE_PRESTATIONS,
    }),
  ],
  [CATEGORIES.MASSAGE]: [
    addOnlyIfAtLeastOneOtherPrestation({
      prestationsWithDependencies: [EYE_CARE],
      dependencies: FACE_CARE_PRESTATIONS,
    }),
  ],
  [CATEGORIES.MASSAGE_WOMAN]: [
    addOnlyIfAtLeastOneOtherPrestation({
      prestationsWithDependencies: [EYE_CARE],
      dependencies: FACE_CARE_PRESTATIONS,
    }),
  ],
  [CATEGORIES.HAIR_REMOVAL]: [
    addSuggestedPrestation({
      prestationsWithDependencies: [HAIR_REMOVAL_BIKINI_LINE_FULL],
      dependencies: [HAIR_REMOVAL_INTERGLUTEAL_CLEFT],
    }),
  ],
  // TODO : DELETE_WHEN_PRO_USE_PRESTATIONS_WITH_OPTIONS_ON_BOOKING
  [CATEGORIES.RECURRING]: [
    addOnlyIfAtLeastOneOtherPrestation({
      prestationsWithDependencies: [CLEANING_PRODUCTS],
      dependencies: [CLEANING_RECURRING, CLEANING_SPOT],
    }),
  ],
};
